import { FC, useEffect, useRef } from "react";
import s from "./PlayCanvasIntegration.module.scss";
import api from "../../../../processes/api";
import { loadConfiguration } from "../../../../utils/functions/playcanvas/loadConfiguration";
import { useAppDispatch } from "../../../../redux/store";
import {
  setEnvironmentVariant,
  updateCameraSetting,
} from "../../../../redux/reducers/ui/uiReducer";
import { ResizableFrame } from "../../../../shared/ResizableFrame";
import { getCameraFov } from "../../../../utils/functions/playcanvas/getCameraFov";

export const PlayCanvasIntegration: FC = () => {
  const containerRef: any = useRef(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    async function getSavedConfig() {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const configParam: any = urlSearchParams.get("config");
      if (configParam) {
        const configJson = await api.getShortUrlApi.getShortUrl(configParam);
        console.log("TEST Loaded config FOV:", configJson.camera.fov);
        try {
          const dataString = JSON.stringify(configJson);
          await loadConfiguration(dataString);
          configJson.environment.map((item: any) => {
            dispatch(
              setEnvironmentVariant({ envName: item.name, envValue: item.val })
            );
          });
        } catch {
          console.error("error");
          // getSavedConfig();
        }
      }
    }
    async function initFovFromScene() {
      try {
        const sceneFov = await getCameraFov();
        if (typeof sceneFov === "number") {
          dispatch(
            updateCameraSetting({
              key: "fov",
              value: sceneFov,
            })
          );
        }
      } catch (error) {
        console.warn(
          "PlayCanvasIntegration: failed to initialize FOV from scene",
          error
        );
      }
    }
    //@ts-ignore
    if (containerRef && !window.firstLoad) {
      console.log(
        "PlayCanvasIntegration: containerRef is not null",
        containerRef
      );
      //@ts-ignore
      window.containerRef = containerRef;
      //@ts-ignore
      window.playCanvasReady = false;
      //@ts-ignore
      window.firstLoad = true;
      containerRef.current.addEventListener("load", () => {
        // Poll for PlayCanvas API readiness instead of fixed timeout
        const pollInterval = 200;
        const maxWaitMs = 30000; // safety cap
        const startedAt = Date.now();

        const poll = setInterval(() => {
          const cw = containerRef.current?.contentWindow as any;

          // Отслеживаем прогресс загрузки ассетов
          if (cw?.pc?.app?.assets) {
            try {
              const assets = cw.pc.app.assets.list();
              const total = assets.length;
              const loaded = assets.filter((a: any) => a.loaded).length;
              const progress = total > 0 ? (loaded / total) * 100 : 0;

              // Отправляем событие с прогрессом загрузки
              window.dispatchEvent(
                new CustomEvent("playcanvas-progress", {
                  detail: { progress, loaded, total },
                })
              );
            } catch (error) {
              // Игнорируем ошибки при проверке ассетов
              console.debug("Error checking asset loading progress:", error);
            }
          }

          if (cw && typeof cw.SetCameraFOV === "function") {
            clearInterval(poll);
            initFovFromScene().finally(() => {
              //@ts-ignore
              window.playCanvasReady = true;
              // Dispatch global event then load saved config
              window.dispatchEvent(new Event("playcanvas-ready"));
              getSavedConfig().finally(() => {
                // Optional notification for listeners after config has loaded
                window.dispatchEvent(new Event("playcanvas-config-loaded"));
              });
            });
          } else if (Date.now() - startedAt > maxWaitMs) {
            clearInterval(poll);
            console.warn("PlayCanvas API did not become ready in time");
          }
        }, pollInterval);
      });
    }
  }, [containerRef]);

  // Attach pointer-lock behavior to the PlayCanvas canvas inside the iframe
  useEffect(() => {
    const onReady = () => {
      const iframeEl = containerRef.current as HTMLIFrameElement | null;
      const doc =
        iframeEl?.contentDocument || iframeEl?.contentWindow?.document || null;
      const canvas = (doc?.getElementById("application-canvas") ||
        null) as HTMLCanvasElement | null;
      if (!doc || !canvas) return;

      // Prevent context menu on right click
      const preventContextMenu = (e: Event) => e.preventDefault();
      canvas.addEventListener("contextmenu", preventContextMenu);

      // Request pointer lock on RMB down inside canvas
      const handleMouseDown = (e: MouseEvent) => {
        if (e.button === 2) {
          // some browsers require calling on the element's ownerDocument
          if (doc.pointerLockElement !== canvas) {
            canvas.requestPointerLock?.();
          }
        }
      };

      // Exit pointer lock on RMB up
      const handleMouseUp = (e: MouseEvent) => {
        if (e.button === 2 && doc.pointerLockElement === canvas) {
          doc.exitPointerLock?.();
        }
      };

      // Safety: exit on iframe blur
      const handleBlur = () => {
        if (doc.pointerLockElement === canvas) {
          doc.exitPointerLock?.();
        }
      };

      canvas.addEventListener("mousedown", handleMouseDown);
      canvas.addEventListener("mouseup", handleMouseUp);
      doc.addEventListener("visibilitychange", handleBlur);

      // Propagate pointer downs inside iframe to parent for UI (e.g., closing dropdowns)
      const handleIframePointerDown = () => {
        window.dispatchEvent(new Event("iframe-pointerdown"));
      };
      doc.addEventListener("pointerdown", handleIframePointerDown, true);

      // Forward keydown events so global shortcuts still work when iframe is focused
      const forwardKeyDown = (event: KeyboardEvent) => {
        if (event.defaultPrevented) return;

        const syntheticEvent = new KeyboardEvent("keydown", {
          key: event.key,
          code: event.code,
          location: event.location,
          repeat: event.repeat,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          bubbles: true,
          cancelable: true,
        });

        window.dispatchEvent(syntheticEvent);
      };
      doc.addEventListener("keydown", forwardKeyDown, true);

      // Cleanup on unmount or when iframe reloads
      const cleanup = () => {
        canvas.removeEventListener("contextmenu", preventContextMenu);
        canvas.removeEventListener("mousedown", handleMouseDown);
        canvas.removeEventListener("mouseup", handleMouseUp);
        doc.removeEventListener("visibilitychange", handleBlur);
        doc.removeEventListener("pointerdown", handleIframePointerDown, true);
        doc.removeEventListener("keydown", forwardKeyDown, true);
        if (doc.pointerLockElement === canvas) doc.exitPointerLock?.();
      };

      // store for potential external cleanup
      (iframeEl as any).__pl_cleanup__ = cleanup;
    };

    window.addEventListener("playcanvas-ready", onReady);
    return () => {
      window.removeEventListener("playcanvas-ready", onReady);
      const iframeEl = containerRef.current as HTMLIFrameElement | null;
      if (iframeEl && (iframeEl as any).__pl_cleanup__) {
        (iframeEl as any).__pl_cleanup__();
      }
    };
  }, [containerRef]);

  return (
    <div className={s.wrapper}>
      <iframe
        title="scene"
        ref={containerRef}
        src="/playCanvasScene/index.html"
        id="demo"
        width="100%"
        height="100%"
        style={{
          width: "100%",
          height: "100%",
          flex: "1 1 auto",
          border: "none",
          display: "block",
        }}
      ></iframe>
      <ResizableFrame className={s.overlay} />
    </div>
  );
};
